import React from 'react';
import { fetchQuestionnaireStatus, selectQuestionnaireProgress } from '@pec/aion-ui-core/slices/questionnaireStatus';
import { fetchSections } from 'slices/sections';
import { fetchSegmentStatuses } from 'slices/segmentStatuses';
import { getStatusSegmentSections } from 'helpers/helpers';
import { omit } from 'lodash';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { useFeature } from 'flagged';
import { useParams } from 'react-router-dom';

export const useQuestionnaireProgress = () => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<{
    organizationId: string;
  }>();
  const progressVisible = useFeature('AionContractorDashboardQuestionnaireProgressBar');

  const {
    isFetching: isFetchingQuestionnaireStatus,
    lastPublishedDate,
    lastAnsweredDateTimeUtc,
    answersToBePublishedCount,
    error
  } = useTypedSelector(state => state.questionnaireStatus);

  const { publishedProgress, draftAnswersProgress } = useTypedSelector(selectQuestionnaireProgress);

  const { isFetching: isFetchingSections, sections } = useTypedSelector(state => state.sections);
  const { isFetching: isFetchingSegmentStatuses, segmentStatuses } = useTypedSelector(state => state.segmentStatuses);
  const { isFetching: isFetchingMainSubscription, mainSubscription } = useTypedSelector(
    state => state.mainSubscription
  );
  const { organization } = useTypedSelector(state => state.organization);

  const isFetching =
    isFetchingQuestionnaireStatus || isFetchingSections || isFetchingSegmentStatuses || isFetchingMainSubscription;

  const hasChasCompliance = Boolean(organization?.features.includes(OrganizationFeature.ChasOrganizationFields));
  const hideProgressBar = !progressVisible && hasChasCompliance;

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchQuestionnaireStatus({ organizationId }));
      dispatch(fetchSections({ organizationId }));
      dispatch(fetchSegmentStatuses({ organizationId }));
    }
  }, [dispatch, organizationId]);

  const progress = useTypedSelector(selectQuestionnaireProgress);

  const statusSegmentSections = omit(
    getStatusSegmentSections(sections, segmentStatuses, mainSubscription.id, false),
    'null'
  );

  return {
    answersToBePublishedCount,
    error,
    progress,
    statusSegmentSections,
    isFetching,
    hideProgressBar,
    mainSubscription,
    lastPublishedDate,
    lastAnsweredDateTimeUtc,
    publishedProgress,
    draftAnswersProgress
  };
};
